import React, { useState, useContext } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import SspContext from 'selfServicePortal/contexts/SspContext'
import { useTranslation } from 'react-i18next'
import { getCurrency } from 'shared'
import Dropdown from './FormComponents/Dropdown/Dropdown'
import Switcher from './FormComponents/Switcher/Switcher'
import FormInput from './FormComponents/Forminput/FormInput'
import Checkbox from './FormComponents/Checkbox/Checkbox'
import { SpinnerScreen } from 'selfServicePortal/components/ui/Spinner'
import { Screen } from 'selfServicePortal/components/ui/Screen'
import { Card } from 'selfServicePortal/components/ui/Card'
import { Message } from 'selfServicePortal/components/Message/Message'
import { Button } from 'selfServicePortal/components/ui/Button'
import './limit_increase.scss'
import { useNavigate } from 'react-router'
import {
	increaseLimit,
	OccupationType,
	AccommodationType,
	MaritalStatus,
} from 'selfServicePortal/services/LimitService/increaseLimit'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
	conditionallyRequiredNumber,
	RequiredNumber,
} from 'selfServicePortal/utils/zodHelpers'
import { getErrorMessage } from 'selfServicePortal/utils'

interface IncreaseProps {
	desiredLimit: number
}

const IncreaseLimitForm = ({ desiredLimit }: IncreaseProps) => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [isChecked, setIsChecked] = useState(false)
	const [isMortgageDisabled, setIsMortgageDisabled] = useState(false)
	const [isRentalCostDisabled, setIsRentalCostDisabled] = useState(false)
	const [isCarLoanDisabled, setIsCarLoanDisabled] = useState(true)
	const { customerData } = useContext(SspContext)

	const { register } = useForm<IncreaseLimitFormParams>()

	const increaseLimitMutation = useMutation(increaseLimit, {})

	const handleAccomodationTypeChange = (
		event: React.ChangeEvent<HTMLSelectElement>,
	) => {
		const selectedValue = event.target.value
		setIsRentalCostDisabled(selectedValue !== AccommodationType.Renter)
		setIsMortgageDisabled(selectedValue !== AccommodationType.Owner)
	}
	const handleCheckboxChange = () => {
		setIsChecked((prevState) => !prevState)
		setIsCarLoanDisabled(!isCarLoanDisabled)
	}

	const schema = z.object({
		maritalStatus: z.nativeEnum(MaritalStatus),
		childrenInHousehold: z.coerce.number().default(0),
		occupationType: z.nativeEnum(OccupationType),
		accomodationType: z.nativeEnum(AccommodationType),
		netMonthlyIncome: RequiredNumber,
		totalMortgageCosts: conditionallyRequiredNumber(isMortgageDisabled),
		monthlyMortgageCosts: conditionallyRequiredNumber(isMortgageDisabled),
		rentalCosts: conditionallyRequiredNumber(isRentalCostDisabled),
		hasCarLoan: z.boolean(),
		totalCarLoan: isCarLoanDisabled
			? z.coerce.number().default(0)
			: conditionallyRequiredNumber(isCarLoanDisabled),
		montlyCarLoanCost: isCarLoanDisabled
			? z.coerce.number().default(0)
			: conditionallyRequiredNumber(isCarLoanDisabled),
	})
	type IncreaseLimitFormParams = z.infer<typeof schema>
	const methods = useForm<IncreaseLimitFormParams>({
		resolver: zodResolver(schema),
	})

	const onSubmit = async (data: IncreaseLimitFormParams) => {
		const procData = {
			...data,
			totalMortgageCosts: isMortgageDisabled
				? 0
				: data.totalMortgageCosts,
			monthlyMortgageCosts: isMortgageDisabled
				? 0
				: data.monthlyMortgageCosts,
			rentalCosts: isRentalCostDisabled ? 0 : data.rentalCosts,
			totalCarLoan: isCarLoanDisabled ? 0 : data.totalCarLoan,
			montlyCarLoanCost: isCarLoanDisabled ? 0 : data.montlyCarLoanCost,
		}

		increaseLimitMutation.mutate({
			...procData,
			desiredLimit: desiredLimit,
			ssn: String(customerData?.nationalId),
			email: String(customerData?.email),
			mobileNumber: String(customerData?.mobilePhoneNumber),
			partyId: String(customerData?.customerId),
		})
	}
	if (increaseLimitMutation.isLoading) {
		return <SpinnerScreen message={t('ChangeCreditLimitLoading')} />
	}
	if (
		increaseLimitMutation.isError ||
		(increaseLimitMutation.isSuccess &&
			increaseLimitMutation.data.decision !== 'Approved')
	) {
		const errorMessage = increaseLimitMutation.isError
			? getErrorMessage(increaseLimitMutation.error)
			: {
					Approved: t(''),
					Investigation: t('ChangeCreditLimitInvestigation'),
					Rejected: t('ChangeCreditLimitDenied'),
					Invalid: t('ChangeCreditLimitInvalid'),
					Error: t(''),
			  }[increaseLimitMutation.data.decision]

		return (
			<Screen>
				<Card transperentBackground>
					<Card.Content>
						<Message type="fail" message={errorMessage} />
					</Card.Content>
					<Card.Footer>
						<Button
							variant="primary"
							onClick={() => {
								navigate(0)
							}}>
							{t('ChangeCreditLimitContinue')}
						</Button>
					</Card.Footer>
				</Card>
			</Screen>
		)
	}

	if (increaseLimitMutation.isSuccess) {
		return (
			<Screen>
				<Card transperentBackground>
					<Card.Content>
						<Message
							type="success"
							message={t('ChangeCreditLimitSuccess')}
						/>
					</Card.Content>
					<Card.Footer>
						<Button
							variant="primary"
							onClick={() => {
								navigate(0)
							}}>
							{t('ChangeCreditLimitContinue')}
						</Button>
					</Card.Footer>
				</Card>
			</Screen>
		)
	}

	return (
		<Screen>
			<Card>
				<Card.Header>{t('ChangeCreditLimitTitle')}</Card.Header>
				<Card.Content>
					<div className="increase-limit-form">
						<div className="sub-header">
							<h5>{t('DesiredLimit')}</h5>
							<h5>
								{desiredLimit} {getCurrency()}
							</h5>
						</div>
						<FormProvider {...methods}>
							<form onSubmit={methods.handleSubmit(onSubmit)}>
								<Dropdown
									label={t('MaritalStatus')}
									options={Object.values(MaritalStatus).map(
										(o) => ({
											label: t(o),
											value: o,
										}),
									)}
									fieldName="maritalStatus"
								/>
								<Switcher
									label={t('ChildrenInHousehold')}
									fieldName="childrenInHousehold"
								/>
								<Dropdown
									label={t('OccupationType')}
									options={Object.values(OccupationType).map(
										(o) => ({
											label: t(o),
											value: o,
										}),
									)}
									fieldName="occupationType"
								/>
								<FormInput
									label={t('NetMonthlyIncome')}
									fieldName="netMonthlyIncome"
									haveCurrency
								/>
								<Dropdown
									label={t('AccomodationType')}
									options={Object.values(
										AccommodationType,
									).map((o) => ({
										label: t(o),
										value: o,
									}))}
									fieldName="accomodationType"
									onChange={handleAccomodationTypeChange}
								/>
								<FormInput
									label={t('TotalMortgageCosts')}
									fieldName="totalMortgageCosts"
									disabled={isMortgageDisabled}
									haveCurrency
								/>
								<FormInput
									label={t('MonthlyMortgageCosts')}
									fieldName="monthlyMortgageCosts"
									disabled={isMortgageDisabled}
									haveCurrency
								/>
								<FormInput
									label={t('RentalCosts')}
									fieldName="rentalCosts"
									disabled={isRentalCostDisabled}
									haveCurrency
								/>
								<Checkbox
									label={t('CarLoan')}
									fieldName="hasCarLoan"
									handler={handleCheckboxChange}
								/>
								{isChecked && (
									<div>
										<FormInput
											label={t('TotalCarLoan')}
											fieldName="totalCarLoan"
											{...register('totalCarLoan')}
											disabled={isCarLoanDisabled}
											haveCurrency
										/>
										<FormInput
											label={t('MontlyCarLoanCost')}
											fieldName="montlyCarLoanCost"
											{...register('montlyCarLoanCost')}
											disabled={isCarLoanDisabled}
											haveCurrency
										/>
									</div>
								)}
								<div className="button-container">
									<Button variant="primary">
										{t('ChangeCreditLimitContinue')}
									</Button>
								</div>
							</form>
						</FormProvider>
					</div>
				</Card.Content>
			</Card>
		</Screen>
	)
}

export default IncreaseLimitForm
